import { Shield, ShieldCheck, User } from "lucide-react";
import type { ComponentType } from "react";
import { type BuiltinRoleKey, ROLE } from "@/app/roles/types";

type RoleStyle = {
  icon: ComponentType<{ className?: string }>;
  /** Badge tone classes layered over the Badge atom's outline base. */
  className: string;
};

/**
 * Visual treatment per built-in role key. Role badges share a low-chrome
 * container so tables scan consistently; tone and icon carry the permission
 * difference without making one role dominate the column.
 * Unknown/custom role keys fall back to the member style.
 */
const ROLE_STYLES: Record<BuiltinRoleKey, RoleStyle> = {
  [ROLE.orgAdmin]: {
    icon: Shield,
    className:
      "bg-violet-500/10 text-violet-700 ring-1 ring-violet-500/25 ring-inset dark:text-violet-300",
  },
  [ROLE.projectAdmin]: {
    icon: ShieldCheck,
    className:
      "bg-green-500/10 text-green-700 ring-1 ring-green-500/25 ring-inset dark:text-green-300",
  },
  [ROLE.projectMember]: {
    icon: User,
    className:
      "bg-muted/25 text-muted-foreground ring-1 ring-border/80 ring-inset",
  },
};

const DEFAULT_ROLE_STYLE = ROLE_STYLES[ROLE.projectMember];

export const getRoleStyle = (roleKey: string): RoleStyle =>
  // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
  (ROLE_STYLES as Partial<Record<string, RoleStyle>>)[roleKey] ??
  DEFAULT_ROLE_STYLE;
